import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Upload, Loader2, Search, FileSpreadsheet, AlertTriangle, CheckCircle2, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";

const API = "/v2/offline-bookings";

export default function OfflineBookings() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [status, setStatus] = useState("DRAFT");

  const loadDrafts = useCallback(async () => {
    try {
      const { data } = await api.get(`${API}/drafts`, { params: { status } });
      setDrafts(data || []);
    } catch { /* ignore */ }
  }, [status]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  useEffect(() => {
    if (q.trim().length < 2 || customer) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users", { params: { role: "customer", search: q.trim() } });
        setMatches((data || []).slice(0, 8));
      } catch { setMatches([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, customer]);

  const downloadTemplate = async () => {
    if (!customer) return toast.error("Choose the customer first - the form is issued with their Customer ID");
    try {
      const res = await api.get(`${API}/template`, { params: { customer_id: customer.id }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Sendtoo_Booking_Form_${customer.customer_code || customer.id}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Form downloaded with the customer's ID, name and mobile filled in");
    } catch (e) { toast.error("Could not download the form"); }
  };

  const upload = async () => {
    if (!customer) return toast.error("Choose the customer first");
    if (!file) return toast.error("Choose the filled Excel file");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("customer_id", customer.id);
      fd.append("file", file);
      const { data } = await api.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); setFile(null); loadDrafts();
      toast.success(`${data.summary.drafts_created} draft(s) created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { duration: 10000 });
    } finally { setBusy(false); }
  };

  const discard = async (id) => {
    if (!window.confirm("Discard this draft? It will not be booked.")) return;
    try { await api.delete(`${API}/drafts/${id}`); loadDrafts(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Offline Bookings</h1>
          <p className="text-sm text-slate-600 mt-1">
            Upload the customer's filled Excel form to create <b>draft</b> orders without typing. Nothing is booked or charged
            until you open a draft in the Booking Console, complete it and press book.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-slate-900">1. Customer &amp; file</h2>
            <button onClick={downloadTemplate} disabled={!customer} title={customer ? "" : "Choose the customer first"}
                    className="inline-flex items-center gap-2 text-sm text-blue-700 hover:underline disabled:text-slate-400 disabled:no-underline disabled:cursor-not-allowed">
              <Download size={16} /> {customer ? `Download form for ${customer.customer_code ? "#" + customer.customer_code : customer.name}` : "Download form (choose customer first)"}
            </button>
          </div>

          <div className="relative">
            {customer ? (
              <div className="flex items-center justify-between border border-emerald-300 bg-emerald-50 rounded-lg px-3 py-2">
                <div className="text-sm"><b>{customer.name}</b> · {customer.phone} <span className="text-slate-500">· {customer.email}</span></div>
                <button className="text-xs text-slate-600 underline" onClick={() => { setCustomer(null); setQ(""); setResult(null); }}>change</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border border-slate-300 rounded-lg px-3 py-2">
                  <Search size={16} className="text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer by name, phone or email"
                         className="flex-1 outline-none text-sm" />
                </div>
                {matches.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                    {matches.map((c) => (
                      <button key={c.id} onClick={() => { setCustomer(c); setMatches([]); }}
                              className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                        {c.customer_code && <span className="font-mono text-xs text-slate-500 mr-2">#{c.customer_code}</span>}<b>{c.name}</b> · {c.phone} <span className="text-slate-500">· {c.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg px-4 py-4 cursor-pointer hover:bg-slate-50">
            <FileSpreadsheet className="text-emerald-600" />
            <span className="text-sm text-slate-700">{file ? file.name : "Choose the filled .xlsx form (max 5 MB)"}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>

          <p className="text-xs text-slate-500">
            Step 1: choose the customer, then download THEIR form (it is pre-filled with their Customer ID, name and mobile).
            Step 2: fill the rows and upload it here. Safety check: the Customer ID in the file must be the selected customer's,
            otherwise nothing is created. Uploading the same rows twice never creates duplicates.
          </p>
          <button disabled={busy} onClick={upload}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Create drafts
          </button>
        </div>

        {result && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
            <h2 className="font-semibold text-slate-900">Result</h2>
            <div className="text-sm text-slate-700">
              {result.summary.rows_read} row(s) read · <b className="text-emerald-700">{result.summary.drafts_created} draft(s) created</b>
              {" "}· {result.summary.incomplete} incomplete · {result.summary.duplicates} duplicate(s) skipped
            </div>
            {result.name_warning && <div className="text-xs text-amber-700 flex gap-1"><AlertTriangle size={14} />{result.name_warning}</div>}
            {result.duplicates_skipped.length > 0 && (
              <div className="text-xs text-slate-600">Skipped (already uploaded): {result.duplicates_skipped.map((d) => `row ${d.row} ${d.receiver || ""}`).join(", ")}</div>
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-semibold text-slate-900">Drafts</h2>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-sm">
              <option value="DRAFT">Waiting to be booked</option>
              <option value="BOOKED">Booked</option>
              <option value="DISCARDED">Discarded</option>
              <option value="ALL">All</option>
            </select>
          </div>
          {drafts.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No drafts here.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Customer</th><th className="pr-3">Receiver</th><th className="pr-3">To</th>
                  <th className="pr-3">Wt / Boxes</th><th className="pr-3">Needs</th><th></th></tr></thead>
                <tbody>
                  {drafts.map((d) => (
                    <tr key={d.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3">{d.customer_name}<div className="text-xs text-slate-400">{d.source_file} · row {d.excel_row}</div></td>
                      <td className="pr-3">{d.data.receiver_name}<div className="text-xs text-slate-400">{d.data.receiver_phone}</div></td>
                      <td className="pr-3">{d.data.receiver_city} {d.data.receiver_pincode}</td>
                      <td className="pr-3">{d.data.weight ?? "-"} kg / {d.data.boxes ?? "-"}</td>
                      <td className="pr-3 max-w-xs">
                        {d.status !== "DRAFT" ? <span className="text-xs text-slate-500">{d.status}</span> :
                          d.missing.length === 0 ? <span className="text-emerald-700 inline-flex items-center gap-1 text-xs"><CheckCircle2 size={14} />Ready to review</span> :
                            <span className="text-xs text-amber-700">{d.missing.join(", ")}</span>}
                        {d.warnings?.length > 0 && <div className="text-xs text-slate-400">{d.warnings.join("; ")}</div>}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {d.status === "DRAFT" && (<>
                          <button onClick={() => navigate(`/staff/book?draft=${d.id}`)}
                                  className="inline-flex items-center gap-1 text-blue-700 hover:underline mr-3"><ExternalLink size={14} />Open in console</button>
                          <button onClick={() => discard(d.id)} className="text-red-600 hover:text-red-800"><Trash2 size={14} /></button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
